import React from 'react';
import styled from 'styled-components';

import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import LinkClickable from 'components/basic/Link/LinkClickable';
import { useSelectorActiveChain } from 'components/hooks';
import { useWebClientReloader } from 'components/settings/WebClientReloader';
import { KATANA_PERPS, MAINTENANCE_NOTICE } from 'config/constants';
import { dismissNotice } from 'reduxState/notices/noticesReducer';
import { useDispatch } from 'reduxState/store';

import { UNSUPPORTED_BROWSER_TEXT } from '../ErrorBoundary';

import Banner, { LinkSpan } from './Banner';
import { useNotices } from './useNotice';

const Wrapper = styled.div`
  grid-area: notification-notice;
`;

const RedeemIconStyled = styled(MaterialSymbol)`
  margin: -5px 5px -5px;
  vertical-align: middle;
`;

/**
 * The NotificationBar Component is utilized for static top notifications to the user which can not be dismissed.
 * It should not be confused with the "TopBanner" which is used for dismissable notifications to the user.
 */
export default function NotificationNoticeWrap() {
  const dispatch = useDispatch();
  const notices = useNotices('notifications');
  const { activeChain } = useSelectorActiveChain();
  const { updateCountdownRemainingSec, reloadNow } = useWebClientReloader();

  // Automatic reloader - to get rid of old version web clients
  if (updateCountdownRemainingSec !== undefined) {
    return (
      <Wrapper>
        <Banner
          isTopNotification
          notice={{ type: 'warn', message: '', isDismissable: false }}
        >
          <RedeemIconStyled icon="redeem" fontSize="small" /> App update
          available. Please reload to continue using {KATANA_PERPS}.{' '}
          {!!updateCountdownRemainingSec && (
            <span style={{ color: 'var(--warnContent)', opacity: 0.7 }}>
              Automatic reload in {updateCountdownRemainingSec} seconds...
            </span>
          )}
          <LinkSpan>
            <LinkClickable color="inherit" $underline onClick={reloadNow}>
              Reload now
            </LinkClickable>
          </LinkSpan>
        </Banner>
      </Wrapper>
    );
  }

  if (MAINTENANCE_NOTICE) {
    return (
      <Wrapper>
        <Banner
          isTopNotification
          notice={{
            type: 'warn',
            message: `We're experiencing a technical issue that we're working to resolve as quickly as possible. We're sorry for the inconvenience.`,
            isDismissable: false,
          }}
        >
          <MaterialSymbol icon="warning" rounded />
        </Banner>
      </Wrapper>
    );
  }

  const mainNotice = notices?.main;
  const secondaryNotice = notices?.secondary;

  if (mainNotice) {
    return (
      <Wrapper>
        <Banner
          isTopNotification
          notice={mainNotice}
          onClose={() =>
            dispatch(dismissNotice('notifications', mainNotice.id, activeChain))
          }
        />
        {secondaryNotice?.isDismissable && (
          <Banner
            isTopNotification
            notice={secondaryNotice}
            onClose={() =>
              dispatch(
                dismissNotice('notifications', secondaryNotice.id, activeChain)
              )
            }
          />
        )}
      </Wrapper>
    );
  }
  // if (pendingWalletAddress && pendingWalletAddress !== walletAddress) {
  //   return (
  //     <Wrapper>
  //       <Banner
  //         isTopNotification
  //         notice={{
  //           type: 'warn',
  //           message:
  //             'Your software wallet address changed. You might not be able to sign. Connect wallet again for switch to new wallet.',
  //           isDismissable: true,
  //         }}
  //         onClose={() => staticDispatch(updatePendingWalletAddress(undefined))}
  //       />
  //     </Wrapper>
  //   );
  // }

  // No longer needed as only some actions like deposit require chainID to be correct (check there)
  // const activeChainId = getCurrentStateChainConfig(activeChain).chainId || -1;
  // if (
  //   walletType?.startsWith('softwareWeb3') &&
  //   walletChainId &&
  //   String(walletChainId) !== String(activeChainId)
  // ) {
  //   return (
  //     <Wrapper>
  //       <Banner
  //         isTopNotification
  //         notice={{
  //           message: `Your software wallet is on the ${getNetworkChainName(
  //             walletChainId
  //           )} network. You need to change your network to ${getNetworkChainName(
  //             activeChainId
  //           )}`,
  //           type: 'warn',
  //           linkText: activeChain === 'binanceChain' ? 'Learn how' : undefined,
  //           link:
  //             activeChain === 'binanceChain'
  //               ? 'https://academy.binance.com/en/articles/connecting-metamask-to-binance-smart-chain'
  //               : undefined,
  //         }}
  //       />
  //     </Wrapper>
  //   );
  // }

  if (!window?.appCheckedModernBrowser) {
    return (
      <Wrapper>
        <Banner
          isTopNotification
          notice={{
            message: UNSUPPORTED_BROWSER_TEXT,
            type: 'warn',
          }}
        />
      </Wrapper>
    );
  }

  return <Wrapper />;
}
